import React, { useState } from 'react'
import { ChevronUp } from 'lucide-react'
import type { SelectionAction } from '../types'
import { Modal } from '../../Modal'
import { Button } from '../../Button'
import { getPortalContainer } from '../../../lib/portalContainer'
import { useT } from '../../../../app/lib/i18n'
import * as s from '../Table.css'

export function BulkSelectionBar({
  selectedIds,
  actions,
  onClear,
  compact = false,
  getSingleName,
}: {
  selectedIds: string[]
  actions: SelectionAction[]
  onClear: () => void
  /** Narrow chrome: one trigger + larger clear, actions in a modal. */
  compact?: boolean
  /** Resolve a display name when exactly one row is selected. */
  getSingleName?: () => string | undefined
}) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const selectedCount = selectedIds.length

  if (selectedCount <= 0 || actions.length === 0) return null

  const runAction = (action: SelectionAction) => {
    if (action.disabled) return
    const names =
      selectedCount === 1 && getSingleName
        ? (() => {
            const name = getSingleName()
            return name ? [name] : undefined
          })()
        : undefined
    action.onClick(selectedIds, names)
    setOpen(false)
  }

  if (compact) {
    return (
      <div className={s.selectionBarOverlay} role="presentation">
        <div
          className={`${s.selectionBar} ${s.selectionBarCompact}`}
          role="toolbar"
          aria-label={t('shared.listView.bulkActions')}
        >
          <button
            type="button"
            className={s.selectionBarBulkTrigger}
            onClick={() => setOpen(true)}
          >
            <span>
              Bulk actions for {selectedCount}{' '}
              {selectedCount === 1 ? 'item' : 'items'}
            </span>
            <ChevronUp size={18} aria-hidden />
          </button>
          <button
            type="button"
            className={s.selectionBarClearBtn}
            onClick={onClear}
            aria-label={t('shared.table.clearSelection')}
            title={t('shared.table.clearSelection')}
          >
            <svg
              width={18}
              height={18}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.25}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <Modal
          open={open}
          setOpen={setOpen}
          minWidth="360px"
          maxWidth="420px"
          minHeight="auto"
          maxHeight="85vh"
          container={getPortalContainer()}
        >
          <Modal.Header>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
              Bulk actions for {selectedCount}{' '}
              {selectedCount === 1 ? 'item' : 'items'}
            </h3>
          </Modal.Header>
          <Modal.Body>
            <div className={s.selectionBarModalList}>
              {actions.map((action, i) => (
                <button
                  key={i}
                  type="button"
                  className={`${s.selectionBarModalItem}${action.danger ? ' is-danger' : ''}`}
                  disabled={action.disabled}
                  title={
                    action.disabled && action.disabledTooltip
                      ? action.disabledTooltip
                      : undefined
                  }
                  onClick={() => runAction(action)}
                >
                  {action.icon ? (
                    <span className={s.selectionBarModalIcon}>{action.icon}</span>
                  ) : null}
                  {action.label}
                </button>
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="button"
              color="primary"
              variant="solid"
              fullWidth
              onClick={() => setOpen(false)}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    )
  }

  return (
    <div className={s.selectionBarOverlay} role="presentation">
      <div
        className={s.selectionBar}
        role="toolbar"
        aria-label={t('shared.listView.bulkActions')}
      >
        <span className={s.selectionBarCount}>{selectedCount} selected</span>
        <div className={s.selectionBarSep} />
        {actions.map((action, i) => (
          <button
            key={i}
            type="button"
            className={`${s.selectionBarBtn}${action.danger ? ' is-danger' : ''}${action.disabled ? ' is-disabled' : ''}`}
            disabled={action.disabled}
            title={
              action.disabled && action.disabledTooltip
                ? action.disabledTooltip
                : undefined
            }
            onClick={() => runAction(action)}
          >
            {action.icon}
            {action.label}
          </button>
        ))}
        <div className={s.selectionBarSep} />
        <button
          type="button"
          className={s.selectionBarBtn}
          onClick={onClear}
          aria-label={t('shared.table.clearSelection')}
          title={t('shared.table.clearSelection')}
        >
          <svg
            width={14}
            height={14}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  )
}
